import { LRUCache } from 'lru-cache';
import { getEnvVar } from '../env';

const BACKGROUND_CACHE_TTL = 1000 * 60 * 60 * 24; // keep a copy for 1 day so we can refresh it in the background
const DEFAULT_CACHE_TTL = 1000 * 60; // refresh once per minute by default (if not specified)

const CACHE = new LRUCache({
  max: 100,
  ttl: BACKGROUND_CACHE_TTL,
  ignoreFetchAbort: true,
  allowStale: true, // allow stale cache to be returned, and refresh it in the background
});

export const cache = <T,>(
  key: string,
  callback: () => T,
  ttl = DEFAULT_CACHE_TTL,
) => {
  const updateCacheInBackground = (key: string, callback: () => T) => {
    const value = callback() as object;
    CACHE.set(key, value, { ttl: BACKGROUND_CACHE_TTL });
  };

  const status = {} as LRUCache.Status<object>;
  let value = CACHE.get(key, { status });
  if (!value) {
    // we did not have the value in the cache, so we need to fetch it
    value = callback() as object;
    CACHE.set(key, value, { ttl: BACKGROUND_CACHE_TTL });
  } else if (value && !CACHE.has(key)) {
    // we got it from stale cache, so we need to refresh it in the background
    updateCacheInBackground(key, callback);
  } else if (status.now && status.start && status.now - status.start > ttl) {
    // we got it from the long term cache, but the ttl has expired, so we need to refresh it in the background
    updateCacheInBackground(key, callback);
  }
  return value as T;
};

/**
 * Retrieves the revalidation time in seconds from the environment variable `NEXT_PUBLIC_REVALIDATE_TIME_SECONDS`.
 * If the environment variable is not set, it defaults to 60 seconds.
 *
 * @returns {number} The revalidation time in seconds.
 */
export const getRevalidateTime = (): number => {
  return getEnvVar('NEXT_PUBLIC_REVALIDATE_TIME_SECONDS')
    ? parseInt(getEnvVar('NEXT_PUBLIC_REVALIDATE_TIME_SECONDS') ?? '60', 10)
    : 60;
};
